import { streamPrimaryPhoto } from '../api/streamPrimaryPhoto';
import type { PrimaryPhotoStreamEvent } from '../api/scanStreamTypes';

/**
 * Concurrency-limited fan-out for the primary-photo SSE streams.
 *
 * After `POST /inventory/confirm/batch` we need to open one stream
 * per accepted item. Chrome's per-origin HTTP/1.1 cap is 6 and we
 * compete with telemetry + auth-refresh, so we cap in-flight streams
 * at {@link PrimaryPhotoPool.maxConcurrent} (default 4) and queue the
 * rest FIFO. On HTTP/2 the cap is effectively unlimited, but 4 is
 * still visibly parallel and leaves headroom.
 *
 * Each enqueued stream gets its own AbortController. `cancelAll()`
 * aborts everything in-flight AND drains the pending queue (useful
 * when the user navigates away from the Done view).
 *
 * Usage:
 *
 *   const pool = new PrimaryPhotoPool({ maxConcurrent: 4 });
 *   pool.onEvent = (itemId, evt) => dispatch(photoEvent({itemId, evt}));
 *   for (const id of acceptedIds) pool.enqueue(id);
 *   // later:
 *   pool.cancelAll();
 */

export type PrimaryPhotoEventHandler = (
  inventoryItemId: number,
  event: PrimaryPhotoStreamEvent,
) => void;

export type PrimaryPhotoErrorHandler = (
  inventoryItemId: number,
  error: unknown,
) => void;

export interface PrimaryPhotoPoolOptions {
  /** Max SSE streams in flight at once. Default 4 — tuned for
   *  HTTP/1.1 + other concurrent XHRs on the page. */
  maxConcurrent?: number;
  onEvent?: PrimaryPhotoEventHandler;
  onError?: PrimaryPhotoErrorHandler;
}

interface QueuedJob {
  inventoryItemId: number;
  controller: AbortController;
}

export class PrimaryPhotoPool {
  readonly maxConcurrent: number;
  onEvent?: PrimaryPhotoEventHandler;
  onError?: PrimaryPhotoErrorHandler;

  private inFlight = new Map<number, AbortController>();
  private queue: QueuedJob[] = [];
  private closed = false;

  constructor(opts: PrimaryPhotoPoolOptions = {}) {
    this.maxConcurrent = Math.max(1, opts.maxConcurrent ?? 4);
    this.onEvent = opts.onEvent;
    this.onError = opts.onError;
  }

  /** Queue a stream. If headroom exists it starts immediately;
   *  otherwise it waits. Duplicate enqueues of the same id are
   *  ignored — stream is at most once per pool lifetime. */
  enqueue(inventoryItemId: number): void {
    if (this.closed) return;
    if (this.inFlight.has(inventoryItemId)) return;
    if (this.queue.some((j) => j.inventoryItemId === inventoryItemId)) return;

    const controller = new AbortController();
    const job: QueuedJob = { inventoryItemId, controller };

    if (this.inFlight.size < this.maxConcurrent) {
      this.startJob(job);
    } else {
      this.queue.push(job);
    }
  }

  /** Abort every in-flight and pending stream. Idempotent. */
  cancelAll(): void {
    this.closed = true;
    for (const ctrl of this.inFlight.values()) {
      try {
        ctrl.abort();
      } catch {
        /* noop */
      }
    }
    this.inFlight.clear();
    for (const job of this.queue) {
      try {
        job.controller.abort();
      } catch {
        /* noop */
      }
    }
    this.queue.length = 0;
  }

  /** Abort a single in-flight or queued stream by id. */
  cancel(inventoryItemId: number): void {
    const ctrl = this.inFlight.get(inventoryItemId);
    if (ctrl) {
      try {
        ctrl.abort();
      } catch {
        /* noop */
      }
      this.inFlight.delete(inventoryItemId);
      this.drainNext();
      return;
    }
    const queuedIdx = this.queue.findIndex(
      (j) => j.inventoryItemId === inventoryItemId,
    );
    if (queuedIdx !== -1) {
      const job = this.queue[queuedIdx];
      try {
        job.controller.abort();
      } catch {
        /* noop */
      }
      this.queue.splice(queuedIdx, 1);
    }
  }

  /** Diagnostics snapshot. */
  stats(): { inFlight: number; queued: number } {
    return { inFlight: this.inFlight.size, queued: this.queue.length };
  }

  private startJob(job: QueuedJob): void {
    this.inFlight.set(job.inventoryItemId, job.controller);
    void this.runJob(job).finally(() => {
      this.inFlight.delete(job.inventoryItemId);
      this.drainNext();
    });
  }

  private async runJob(job: QueuedJob): Promise<void> {
    try {
      for await (const evt of streamPrimaryPhoto(job.inventoryItemId, {
        signal: job.controller.signal,
      })) {
        this.onEvent?.(job.inventoryItemId, evt);
        // All terminal events close the stream server-side; the
        // generator will exit on the next reader.read() → done.
      }
    } catch (err) {
      // AbortError is expected on cancelAll / cancel — not an error
      // worth surfacing.
      if (job.controller.signal.aborted) return;
      this.onError?.(job.inventoryItemId, err);
    }
  }

  private drainNext(): void {
    if (this.closed) return;
    while (this.inFlight.size < this.maxConcurrent && this.queue.length > 0) {
      const next = this.queue.shift();
      if (next) this.startJob(next);
    }
  }
}
